import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import _ from 'lodash';
import { fileNameEdit } from 'helpers/FileUtils';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/consumi_tikets/');

const format = (v) => `${v}%`;

const defaultState = {
  table1: [],
  table2: [],
  total1: {},
  total2: {},
  barChart: [],
  total: {},
  tableData: [],
  barChangeLine: [],
  keys: [],
  bar2: {
    barChart: [],
    barChangeLine: [],
    tableData: [],
    maxValue: 0,
  },
};

const useInvoicesCategoryTickets = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const [columns, setColumns] = useState([
    {
      title: t('CATEGORIA MERCEOLOGICA'),
      dataIndex: 'Tickets',
      fixed: true,
    },
    {
      title: t('Tickets YTD'),
      dataIndex: 'Tickets YTD',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('Tickets LY'),
      dataIndex: 'Tickets LY',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('Tickets VS LY +/-(%)'),
      dataIndex: 'Tickets VS LY +/-(%)',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Inc. % Tickets YTD'),
      dataIndex: 'Inc. % Tickets YTD',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Inc. % Tickets LY'),
      dataIndex: 'Inc. % Tickets LY',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let table1, table2;

        if (!isAllReports) {
          const {
            data: { table1: backTable1, table2: backTable2 },
          } = await api.getFootfalls({ params });
          table1 = backTable1;
          table2 = backTable2;
        } else {
          const { table1: backTable1, table2: backTable2 } = allReportsData;
          table1 = backTable1;
          table2 = backTable2;
        }

        if (!table1 || !table2) return;

        const maxValueFroLy1 = _.maxBy(table1.data, 'Inc. % Tickets YTD')[
          'Inc. % Tickets YTD'
        ];
        const maxValueFroYtd1 = _.maxBy(table1.data, 'Inc. % Tickets LY')[
          'Inc. % Tickets LY'
        ];

        const maxValue1 =
          maxValueFroLy1 > maxValueFroYtd1 ? maxValueFroLy1 : maxValueFroYtd1;

        const maxValueFroLy2 = _.maxBy(table2.data, 'Inc. % Tickets YTD')[
          'Inc. % Tickets YTD'
        ];
        const maxValueFroYtd2 = _.maxBy(table2.data, 'Inc. % Tickets LY')[
          'Inc. % Tickets LY'
        ];

        const maxValue2 =
          maxValueFroLy2 > maxValueFroYtd2 ? maxValueFroLy2 : maxValueFroYtd2;

        const barChart = table1.data.map((item) => ({
          date: item['Tickets'],
          'Inc. % Tickets YTD': item['Inc. % Tickets YTD'],
          'Inc. % Tickets YTDColor': '#61cdbb',
          'Inc. % Tickets LY': item['Inc. % Tickets LY'],
          'Inc. % Tickets LYColor': '#f47560',
        }));

        const barChangeLine = {
          id: 'Tickets VS LY +/-(%)',
          data: table1.data.map((item) => ({
            x: item['Tickets'],
            y: item['Tickets VS LY +/-(%)'],
            tooltipKeys: [
              {
                key: 'Inc. % Tickets YTD',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Inc. % Tickets YTD'])),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Inc. % Tickets LY',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Inc. % Tickets LY'])),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Tickets VS LY +/-(%)',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Tickets VS LY +/-(%)'])),
                ),
                color: '#1f77b4',
                percent: '',
              },
            ],
          })),
        };

        const bar2Chart2 = table2.data.map((item) => ({
          date: item['Tickets'],
          'Inc. % Tickets YTD': item['Inc. % Tickets YTD'],
          'Inc. % Tickets YTDColor': '#61cdbb',
          'Inc. % Tickets LY': item['Inc. % Tickets LY'],
          'Inc. % Tickets LYColor': '#f47560',
        }));

        const bar2ChangeLine = {
          id: 'Tickets VS LY +/-(%)',
          data: table2.data.map((item) => ({
            x: item['Tickets'],
            y: item['Tickets VS LY +/-(%)'],
            tooltipKeys: [
              {
                key: 'Inc. % Tickets YTD',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Inc. % Tickets YTD'])),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Inc. % Tickets LY',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Inc. % Tickets LY'])),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Tickets VS LY +/-(%)',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(format(item['Tickets VS LY +/-(%)'])),
                ),
                color: '#1f77b4',
                percent: '',
              },
            ],
          })),
        };

        setData({
          table1: table1.data,
          table2: table2.data,
          total1: table1.total,
          total2: table2.total,
          maxValue: maxValue1,
          barChart,
          tableData: [],
          barChangeLine: [barChangeLine],
          bar2: {
            barChart: bar2Chart2,
            barChangeLine: [bar2ChangeLine],
            tableData: [],
            maxValue: maxValue2,
          },
        });
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  const summary = useCallback(() => {
    const { total1 = {} } = data;

    delete total1['Tickets'];

    const tableKeys = Object.keys(total1);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total1[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data, columns]);

  const summary2 = useCallback(() => {
    const { total2 = {} } = data;

    delete total2['Tickets'];

    const tableKeys = Object.keys(total2);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total2[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  const columns2 = useMemo(() => {
    return [
      {
        title: t('Segmento'),
        dataIndex: 'Tickets',
        fixed: true,
      },
      {
        title: t('Tickets YTD'),
        dataIndex: 'Tickets YTD',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('Tickets LY'),
        dataIndex: 'Tickets LY',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('Tickets VS LY +/-(%)'),
        dataIndex: 'Tickets VS LY +/-(%)',
        render: (v) =>
          Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
      },
      {
        title: t('Inc. % Tickets YTD'),
        dataIndex: 'Inc. % Tickets YTD',
        render: (v) =>
          Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
      },
      {
        title: t('Inc. % Tickets LY'),
        dataIndex: 'Inc. % Tickets LY',
        render: (v) =>
          Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
      },
    ];
  }, []);

  return {
    columns2,
    columns,
    data,
    filters,
    format,
    title,
    loading,
    handleDownloadExcel,
    searchParams,
    summary,
    summary2,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesCategoryTickets;
